import type {
  PropertyDetailResponseDto,
  PropertyDetailWireData,
  PropertyDetailWireFinancials,
  PropertyDetailWireFee,
} from './propertyDetailContracts'
import type {
  PropertyDetail,
  PropertyDetailFee,
  PropertyFinancials,
} from '../types/propertyDetailTypes'
import type { AppError } from '@core/api/normalizeError'

// The detail endpoint wraps its payload in `{ success, data, message? }`.
// Unwrap, normalise nulls/defaults, and pass through as the camelCase view
// model. Surface "success: false" as an AppError-shaped throw so TanStack
// Query publishes it via `error` with the same plain-object contract every
// container reads via `error.value` (typed AppError app-wide).
export function parsePropertyDetail(resp: PropertyDetailResponseDto): PropertyDetail {
  if (!resp || typeof resp !== 'object' || !('data' in resp)) {
    const error: AppError = { message: 'Invalid property detail response.' }
    throw error
  }
  if (resp.success === false || !resp.data) {
    const error: AppError = { message: resp.message ?? 'Failed to load property detail.' }
    throw error
  }
  return toViewModel(resp.data)
}

// Everything that is not a digit, sign or decimal point: "AED 1,100,000" →
// "1100000", "7.2%" → "7.2".
const NON_NUMERIC_RE = /[^\d.-]/g

/**
 * Reads a number the backend may send as a number, a numeric string or a
 * display string ("AED 60000", "6.5%"). Empty / unparseable → null.
 */
export const parseDisplayNumber = (value: number | string | null | undefined): number | null => {
  if (value == null) return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const cleaned = value.replace(NON_NUMERIC_RE, '')
  if (!cleaned) return null
  const num = Number(cleaned)
  return Number.isFinite(num) ? num : null
}

const toFinancials = (wire: PropertyDetailWireFinancials | null): PropertyFinancials => ({
  currentMarketValue: parseDisplayNumber(wire?.currentMarketValue),
  grossYieldPct: parseDisplayNumber(wire?.grossYieldPercentage),
  netYieldPct: parseDisplayNumber(wire?.netYieldPercentage),
  annualRentalIncome: parseDisplayNumber(wire?.annualRentalIncome),
  netRentalIncome: parseDisplayNumber(wire?.netRentalIncome),
  annualServiceChargeTotal: parseDisplayNumber(wire?.annualServiceChargeTotal),
})

const toFee = (fee: PropertyDetailWireFee): PropertyDetailFee => ({
  code: fee.code,
  label: fee.label,
  lifecycleStage: fee.lifecycleStage ?? null,
  paidBy: fee.paidBy ?? null,
  recurrence: fee.recurrence ?? null,
  rate: fee.rate?.trim() || null,
})

const trimOrNull = (value: string | null | undefined): string | null => value?.trim() || null

const toViewModel = (wire: PropertyDetailWireData): PropertyDetail => ({
  offeringId: wire.offeringId,
  propertyId: wire.propertyId,
  offeringTitle: wire.offeringTitle,
  offeringDescription: wire.offeringDescription,
  offeringStatus: wire.offeringStatus,
  listingPrice: wire.listingPrice,
  purchasePrice: parseDisplayNumber(wire.purchasePrice),
  totalTokens: wire.totalTokens,
  tokensSold: wire.tokensSold,
  tokensAvailable: wire.tokensAvailable,
  tokenPrice: wire.tokenPrice,
  minInvestmentAmount: wire.minInvestmentAmount,
  maxInvestmentAmount: wire.maxInvestmentAmount,
  totalRaised: wire.totalRaised,
  totalInvestors: wire.totalInvestors,
  totalRentalIncome: parseDisplayNumber(wire.totalRentalIncome),
  investmentWindowStart: wire.investmentWindowStart ?? '',
  investmentWindowEnd: wire.investmentWindowEnd ?? '',
  targetRoi5yr: parseDisplayNumber(wire.targetRoi5yr),
  annualRoiPct: parseDisplayNumber(wire.annualRoiPercentage),
  expectedRentalYield: parseDisplayNumber(wire.expectedRentalYield),
  expectedCapitalAppreciation: parseDisplayNumber(wire.expectedCapitalAppreciation),
  rentalDistributionFrequency: wire.rentalDistributionFrequency,
  term: wire.term,
  propertyName: wire.propertyName,
  propertyType: wire.propertyType,
  propertyStatus: wire.propertyStatus,
  propertyDescription: wire.propertyDescription,
  propertyShortDescription: trimOrNull(wire.propertyShortDescription),
  addressLine1: wire.addressLine1,
  addressLine2: wire.addressLine2,
  city: wire.city,
  state: wire.state,
  builtUpAreaSqm: wire.builtUpAreaSqm,
  actualAreaSqm: wire.actualAreaSqm ?? null,
  numberOfBedrooms: wire.numberOfBedrooms,
  numberOfBathrooms: wire.numberOfBathrooms,
  parkingSpaces: wire.parkingSpaces,
  yearBuilt: wire.yearBuilt,
  isFurnished: wire.isFurnished ?? false,
  developerName: wire.developerName,
  areaName: wire.areaName,
  buildingName: wire.buildingName,
  latitude: wire.latitude,
  longitude: wire.longitude,
  googleMapsUrl: wire.googleMapsUrl,
  fundedPercentage: wire.fundedPercentage,
  availableAmount: wire.availableAmount,
  daysToClose: wire.daysToClose,
  daysToLaunch: wire.daysToLaunch,
  isBookmarked: wire.isBookmarked,
  isNotifySubscribed: Boolean(wire.isNotifySubscribed),
  financials: toFinancials(wire.financials),
  fees: (wire.fees ?? []).map(toFee),
  media: (wire.media ?? []).map((media) => ({
    id: media.id,
    filePath: media.filePath,
    mediaType: media.mediaType,
    isCover: media.isCover,
    isFeatured: media.isFeatured,
    displayOrder: media.displayOrder,
    categoryName: media.categoryName,
  })),
  documents: (wire.documents ?? []).map((doc) => ({
    id: doc.id,
    documentType: doc.documentType,
    documentCategory: doc.documentCategory ?? null,
    documentTitle: doc.documentTitle,
    filePath: doc.filePath,
    fileName: doc.fileName,
    mimeType: doc.mimeType,
  })),
  highlights: (wire.highlights ?? []).map((highlight) => ({
    id: highlight.id,
    highlightText: highlight.highlightText,
    highlightBrief: highlight.highlightBrief,
    highlightIcon: highlight.highlightIcon,
    displayOrder: highlight.displayOrder,
  })),
  amenities: (wire.amenities ?? []).map((amenity) => ({
    id: amenity.id,
    name: amenity.name,
    icon: amenity.icon,
    isCustom: amenity.isCustom,
    groupName: amenity.groupName,
    notes: amenity.notes,
  })),
})
